import { useState, useEffect, useCallback } from 'react';
import InputQuestionGroup from '../../components/InputQuestionGroup';
import { InputField } from '@jobtarget/ui-library';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLayout } from '../../context/LayoutContext';
import Button from '../../components/Button';
import Breadcrumb from '../../components/Breadcrumb';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormBuilderService } from '../../api/FormBuilderService';
import { IQuestionnaire } from '../../types/questionnaire';
import '../../components/css/CustomElements.css';
import useResponsive from '../../hooks/useResponsive';
import Spinner from '../../components/Spinner';
import { useParams } from 'react-router-dom';
import { getWithExpiry } from '../../utils/sessionStorage';
import { getCompanyIdFromToken, getDecodedToken } from '../../utils/tokenHelper';

const MAX_QUESTIONS = 8; // Define maximum number of questions allowed

const QuestionnaireBuilder = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const location = useLocation();
  const { isCloning, cloneData, isTemplate, templateData } = location.state || {};
  const { setHeaderContent } = useLayout();
  const navigate = useNavigate();
  const isMobile = useResponsive('md');
  const decodedToken = getDecodedToken();
  const companyId = decodedToken?.companyId || getWithExpiry('companyId') || 1;
  const userId = decodedToken?.userId;

  console.log("companyId from questionnaire builder", companyId);
  console.log("userId from questionnaire builder", userId); 
  const [errors, setErrors] = useState({
    questionnaireName: '',
    contentType: '',
    questions: '',
  });
  const [formData, setFormData] = useState({
    questionnaireName: '',
    contentType: '',
    companyId: companyId,
    questions: [{ text: '', type: 'text' as const, required: true, }],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existedUserId, setExistedUserId] = useState<number | null>(null);

  // Add effect to handle clone data
  useEffect(() => {
    if (isCloning && cloneData) {
      setFormData({
        questionnaireName: cloneData.questionnaireName,
        contentType: cloneData.contentType.toLowerCase(),
        companyId: companyId,
        questions: cloneData.questions
      });
    }
  }, [isCloning, cloneData, companyId]);

  // Add effect to handle template data
  useEffect(() => {
    if (isTemplate && templateData) {
      setFormData({
        questionnaireName: templateData.questionnaireName,
        contentType: templateData.contentType.toLowerCase(),
        companyId: companyId,
        questions: templateData.questions.map((q: any) => ({
          text: q.text,
          type: q.type || 'text',
          required: q.required !== undefined ? q.required : true
        }))
      });
    }
  }, [isTemplate, templateData, companyId]);

  // Add new useEffect to fetch questionnaire data when editing
  useEffect(() => {
    const fetchQuestionnaireData = async () => {
      if (questionnaireId) {
        try {
          const response = await FormBuilderService.getQuestionnaire(questionnaireId);
          if (response.userId) {
            setExistedUserId(response.userId);
          }
          setFormData({
            questionnaireName: response.questionnaireName,
            contentType: response.contentType.toLowerCase(),
            companyId: response.companyId,
            questions: response.questions.map(q => ({
              text: q.questionText || q.text || '',
              type: q.type.toLowerCase() as 'text',
              required: q.required
            }))
          });
        } catch (error) {
          console.error('Error fetching questionnaire:', error);
          alert('Failed to load questionnaire data');
        }
      }
    };

    fetchQuestionnaireData();
  }, [questionnaireId]);

  const handleSave = useCallback(async () => {
    try {
      const validationError = validateForm();
      if (validationError) {
        return;
      }

      setIsSubmitting(true);

      // Get userId from token
      const decodedToken = getDecodedToken();
      const userId = decodedToken?.userId;

      // Add userId to form data only if it doesn't exist
      const finalFormData = {
        ...formData,
        userId: existedUserId ? existedUserId : userId
      } as IQuestionnaire;

      let response;
      if (questionnaireId) {
        // Update existing questionnaire
        response = await FormBuilderService.updateQuestionnaire(
          questionnaireId,
          finalFormData as IQuestionnaire
        );
        // Navigate to questionnaires tab for updates
        navigate('/v1/recruiter?tab=questionnaires');
      } else {
        // Create new questionnaire
        response = await FormBuilderService.createQuestionnaire(finalFormData as IQuestionnaire);
        // Navigate to home with the created questionnaire ID in state.
        // When this creation came from a Clone flow, also forward the clone marker
        // and (current) questionnaire name so the post-attach toast can match Figma:
        // "{questionnaireName} cloned and attached to jobs."
        const newId = response.id || response.questionnaireId;
        navigate('/v1/recruiter?tab=questionnaires', {
          state: isCloning
            ? {
                newQuestionnaireId: newId,
                isCloning: true,
                questionnaireName: finalFormData.questionnaireName,
              }
            : { newQuestionnaireId: newId, questionnaireName: finalFormData.questionnaireName },
        });
      }
    } catch (error: any) {
      console.error('Error saving questionnaire:', error);
      alert(error?.response?.status === 409 
        ? 'The questionnaire already exists. Would you like to modify it instead?' 
        : 'Failed to save questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, navigate, questionnaireId, isCloning]);

  const validateForm = () => {
    const newErrors = {
      questionnaireName: '',
      contentType: '',
      questions: '',
    };

    if (formData.questionnaireName === '') {
      newErrors.questionnaireName = 'This field is required!';
    }
    if (formData.contentType === '') {
      newErrors.contentType = 'Please select an Answer Format (audio or video)';
    }
    if (formData.questions.length === 0) {
      newErrors.questions = 'Atleast one question is required!';
    }
    if (formData.questions.some(question => question.text === '')) {
      newErrors.questions = 'Question text is required!';
    }

    setErrors(newErrors);
    return Object.values(newErrors).some(error => error !== '');
  };

  const handleClose = useCallback(() => {
    if (window.confirm('Are you sure you want to leave?')) {
      setHeaderContent(null);
      navigate('/v1/recruiter?tab=questionnaires');
    }
  }, [navigate, setHeaderContent]);

  useEffect(() => {
    setHeaderContent(
      <div style={{ display: 'flex', gap: '8px' }}>
      </div>
    );
  }, [setHeaderContent, handleClose, handleSave]);

  const handleContentTypeChange = (type: 'audio' | 'video') => {
    setFormData(prev => ({
      ...prev,
      contentType: type,
    }));
  };

  const addQuestion = () => {
    if (formData.questions.length >= MAX_QUESTIONS) {
      alert(`Maximum number of questions (${MAX_QUESTIONS}) reached.`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { text: '', type: 'text', required: true }],
    }));
  };

  const removeQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const moveQuestion = useCallback((dragIndex: number, hoverIndex: number) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const [draggedQuestion] = newQuestions.splice(dragIndex, 1);
      newQuestions.splice(hoverIndex, 0, draggedQuestion);
      return {
        ...prev,
        questions: newQuestions,
      };
    });
  }, []);

  return (
    <div style={{ textAlign: 'left' }}>
      {isSubmitting && <Spinner fullScreen label="Saving questionnaire..." color="primary" />}
      <Breadcrumb
        items={[
          { text: 'Home', href: '/v1/recruiter?tab=questionnaires' },
          { text: 'Builder', href: '/v1/questionnaire', active: true },
        ]}
      />
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
      >
        <h3 style={{ textAlign: 'center', paddingBottom: '1.5rem' }}>Questionnaire Builder</h3>
        <div
          style={{
            width: isMobile ? '100%' : '60%',
            backgroundColor: '#f8f9fa',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ marginBottom: '1rem' }}>
          <label 
            htmlFor="questionnaireName" 
            style={{ 
              fontWeight: 500, 
              display: 'block' 
            }}
          >
            Questionnaire Name
            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
          </label>
            <InputField
              id="questionnaireName"
              name="questionnaireName"
              value={formData.questionnaireName}
              onChange={e => setFormData(prev => ({ ...prev, questionnaireName: e.target.value }))}
              onPaste={e => {
                const pastedText = e.clipboardData.getData('text');
                setFormData(prev => ({ ...prev, questionnaireName: pastedText }));
              }}
              placeholder="Untitled"
            />
            {errors.questionnaireName && (
              <div
                style={{
                  color: 'red',
                  marginBottom: '1rem',
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                }}
              >
                {errors.questionnaireName}
              </div>
            )}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>
              Answer Format:
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0rem' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    id="audioFormat"
                    name="contentType"
                    value="audio"
                    checked={formData.contentType === 'audio'}
                    onChange={() => handleContentTypeChange('audio')}
                    className="custom-checkbox"
                    style={{
                      marginTop: '0.2rem',
                      width: '1.0rem',
                      height: '1.0rem',
                    }}
                  />
                  <label
                    htmlFor="audioFormat"
                    style={{ marginLeft: '0.2rem', marginTop: '0.5rem', color: '#3f4254' }}
                  >
                    Audio
                  </label>
                  <img
                    src="/svgs/mic.svg"
                    alt="Microphone"
                    style={{
                      width: '1.2rem',
                      height: '1.2rem',
                      marginLeft: '0.2rem',
                      color: '#6c757d',
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    id="videoFormat"
                    name="contentType"
                    value="video"
                    checked={formData.contentType === 'video'}
                    onChange={() => handleContentTypeChange('video')}
                    className="custom-checkbox"
                    style={{
                      marginTop: '0.2rem',
                      width: '1.0rem',
                      height: '1.0rem',
                    }}
                  />
                  <label
                    htmlFor="videoFormat"
                    style={{ marginLeft: '0.2rem', marginTop: '0.5rem', color: '#3f4254' }}
                  >
                    Video
                  </label>
                  <img
                    src="/svgs/video.svg"
                    alt="Video camera"
                    style={{
                      width: '1.2rem',
                      height: '1.2rem',
                      marginLeft: '0.2rem',
                      color: '#6c757d',
                    }}
                  />
                </div>
              </div>
            </div>
            {errors.contentType && (
              <div
                style={{
                  color: 'red',
                  marginBottom: '1rem',
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                }}
              >
                {errors.contentType}
              </div>
            )}
          </div>

          <div>
            <label
              style={{
                fontWeight: 500,
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '1.1rem',
              }}
            >
            </label>
            <DndProvider backend={HTML5Backend}>
              {formData.questions.map((question, index) => (
                <InputQuestionGroup
                  key={index}
                  id={`question-${index}`}
                  index={index}
                  buttonText={(index + 1).toString() + '.'}
                  moveQuestion={moveQuestion}
                  onRemove={() => removeQuestion(index)}
                  value={question.text}
                  onChange={value => {
                    const newQuestions = [...formData.questions];
                    newQuestions[index] = { ...newQuestions[index], text: value };
                    setFormData(prev => ({ ...prev, questions: newQuestions }));
                  }}
                />
              ))}
            </DndProvider>
            {errors.questions && (
              <div
                style={{
                  color: 'red',
                  marginBottom: '1rem',
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                }}
              >
                {errors.questions}
              </div>
            )}
          </div>

          <Button
            text={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/svgs/plus.svg"
                  alt="Plus"
                  className="plus-icon"
                  style={{
                    width: '0.8rem',
                    height: '0.8rem',
                    marginRight: '0.5rem',
                  }}
                />
                Add Question
              </div>
            }
            onClick={addQuestion}
            variant="outline-primary"
            id="add-question-button"
            style={{ 
              marginTop: '0.6rem',
              opacity: formData.questions.length >= MAX_QUESTIONS ? 0.5 : 1,
              cursor: formData.questions.length >= MAX_QUESTIONS ? 'not-allowed' : 'pointer'
            }}
            disabled={formData.questions.length >= MAX_QUESTIONS}
          />
          {formData.questions.length >= MAX_QUESTIONS && (
            <div style={{
              color: 'red',
              marginBottom: '1rem',
              fontSize: '0.8rem',
              marginTop: '0.5rem',
            }}>
              Maximum number of questions reached.
            </div>
          )}
        </div>
      </div>
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        // marginTop: '2rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          width: isMobile ? '100%' : '60%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem',
          backgroundColor: '#fff',
          border: '1px solid #EBEDF3',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px 0px rgba(173, 181, 189, 0.15), 0px 1px 2px 0px rgba(173, 181, 189, 0.30)'
        }}>
          <Button text="Cancel" variant="outline-primary" onClick={handleClose} />
          <Button text="Create" variant="primary" onClick={handleSave} />
        </div>
      </div>

    </div>
  );
};

export default QuestionnaireBuilder;
